<template>
  <main class="leads-agent">
    <section class="leads-header">
      <div class="leads-title">
        <div class="agent-icon">
          <v-icon color="white" size="30">mdi-bullhorn-outline</v-icon>
        </div>
        <div>
          <h1>Agente de Leads</h1>
          <p>
            Crea prospectos, asígnalos a un workflow y deja que el agente
            precalifique oportunidades con comunicación automatizada.
          </p>
        </div>
      </div>

      <div class="header-actions">
        <v-btn
          outlined
          class="details-button"
          :loading="syncingLeads"
          :disabled="syncingLeads"
          @click="syncLeads"
        >
          <v-icon left size="20">mdi-sync</v-icon>
          Sincronizar
        </v-btn>
        <v-btn class="primary-action" elevation="0" @click="uploadDialog = true">
          <v-icon left size="20">mdi-account-plus-outline</v-icon>
          Crear lead
        </v-btn>
      </div>
    </section>

    <section class="insight-banner">
      <div>
        <span>Prospección asistida</span>
        <strong>
          El agente contacta prospectos, detecta BANT, actualiza estatus y
          entrega contexto listo para que ventas tome la conversación.
        </strong>
      </div>
      <button type="button" @click="openBulkUploadDialog">
        Carga masiva
        <v-icon size="18">mdi-arrow-right</v-icon>
      </button>
    </section>

    <section class="metrics-grid">
      <article v-for="metric in metrics" :key="metric.label" class="metric-card">
        <div>
          <span>{{ metric.label }}</span>
          <strong>{{ metric.value }}</strong>
          <small>{{ metric.helper }}</small>
        </div>
        <v-icon :color="metric.color" size="27">{{ metric.icon }}</v-icon>
      </article>
    </section>

    <section class="tabs-row">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        :class="{ active: activeTab === tab.value, disabled: tab.disabled }"
        :disabled="tab.disabled"
        :title="tab.disabled ? 'Próximamente' : ''"
        @click="!tab.disabled && (activeTab = tab.value)"
      >
        {{ tab.label }}
      </button>
    </section>

    <section v-if="activeTab === 'leads'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Leads en seguimiento</h2>
          <p>
            Filtra, prioriza y revisa el avance de prospección sin perder contexto.
          </p>
        </div>
        <v-btn text class="link-action" @click="uploadDialog = true">
          <v-icon left size="18">mdi-account-plus-outline</v-icon>
          Crear lead
        </v-btn>
      </div>

      <v-alert
        v-if="leadsError"
        dense
        outlined
        type="error"
        class="agent-alert"
      >
        {{ leadsError }}
        <button type="button" class="alert-action" @click="getLeads">
          Reintentar
        </button>
      </v-alert>

      <v-progress-linear
        v-if="loadingLeads"
        indeterminate
        color="primary"
        class="agent-progress"
      />

      <p v-if="lastSyncedAt" class="sync-caption">
        Última sincronización: {{ formatDateTime(lastSyncedAt) }}
      </p>

      <div class="crm-toolbar">
        <label class="search-field">
          <v-icon size="20">mdi-magnify</v-icon>
          <input
            v-model="leadFilters.search"
            type="text"
            placeholder="Buscar por lead, empresa, canal o perfil"
          />
        </label>

        <select v-model="leadFilters.status">
          <option value="all">Todos los estatus</option>
          <option
            v-for="status in leadStatuses"
            :key="status.value"
            :value="status.value"
          >
            {{ status.label }}
          </option>
        </select>

        <select v-model="leadFilters.channel">
          <option value="all">Todos los canales</option>
          <option value="WhatsApp">WhatsApp</option>
          <option value="Email">Email</option>
          <option value="Llamada">Llamada</option>
        </select>

        <div class="view-switch">
          <button
            type="button"
            :class="{ active: leadViewMode === 'table' }"
            @click="leadViewMode = 'table'"
          >
            <v-icon size="18">mdi-table</v-icon>
            Tabla
          </button>
          <button
            type="button"
            :class="{ active: leadViewMode === 'pipeline' }"
            @click="leadViewMode = 'pipeline'"
          >
            <v-icon size="18">mdi-view-column-outline</v-icon>
            Pipeline
          </button>
        </div>
      </div>

      <div v-if="leadViewMode === 'table'" class="crm-table">
        <div class="crm-table-head">
          <span>Lead</span>
          <span>Empresa</span>
          <span>Canal</span>
          <span>Estatus</span>
          <span>Última interacción</span>
          <span>Siguiente acción</span>
        </div>

        <button
          v-for="lead in filteredLeads"
          :key="lead.id"
          type="button"
          class="crm-table-row"
          @click="openLead(lead)"
        >
          <span>
            <strong>{{ lead.name }}</strong>
            <small>{{ lead.profile }}</small>
          </span>
          <span>{{ lead.company }}</span>
          <span>
            <v-icon size="17">{{ lead.channelIcon }}</v-icon>
            {{ lead.channel }}
          </span>
          <span>
            <em class="status-pill" :class="lead.statusClass">{{ lead.status }}</em>
          </span>
          <span>{{ lead.lastContact }}</span>
          <span class="next-step-cell" :title="lead.nextStep">{{ lead.nextStep }}</span>
        </button>

        <div v-if="!loadingLeads && !filteredLeads.length" class="empty-state">
          No hay leads con esos filtros. Sincroniza para consultar los últimos cambios.
        </div>
      </div>

      <div v-else class="pipeline-board">
        <section v-for="stage in pipelineStages" :key="stage.value" class="pipeline-column">
          <header>
            <span>{{ stage.label }}</span>
            <strong>{{ leadsByStage(stage.value).length }}</strong>
          </header>

          <button
            v-for="lead in leadsByStage(stage.value)"
            :key="lead.id"
            type="button"
            class="pipeline-card"
            @click="openLead(lead)"
          >
            <strong>{{ lead.name }}</strong>
            <span>{{ lead.company }}</span>
            <small>
              <v-icon size="15">{{ lead.channelIcon }}</v-icon>
              {{ lead.lastContact }}
            </small>
            <em>{{ lead.bantProgress }}</em>
          </button>

          <div v-if="!leadsByStage(stage.value).length" class="pipeline-empty">
            Sin leads
          </div>
        </section>
      </div>

      <div v-if="leadPagination.total > leadPagination.limit" class="table-pagination">
        <span>
          {{ leadPagination.total }} leads
        </span>
        <v-pagination
          v-model="leadPage"
          :length="leadPageCount"
          :total-visible="6"
          color="primary"
        />
      </div>
    </section>

    <v-navigation-drawer
      v-model="leadDrawer"
      fixed
      right
      temporary
      width="620"
      class="lead-drawer"
    >
      <div v-if="selectedLead" class="lead-drawer-content">
        <header class="drawer-header">
          <div>
            <h2>{{ selectedLead.name }}</h2>
            <p>{{ selectedLead.company }} · {{ selectedLead.profile }}</p>
          </div>
          <button type="button" @click="leadDrawer = false">
            <v-icon size="22">mdi-close</v-icon>
          </button>
        </header>

        <div class="drawer-status-row">
          <span class="status-pill" :class="selectedLead.statusClass">
            {{ selectedLead.status }}
          </span>
          <span>{{ selectedLead.bantProgress }}</span>
          <span>
            <v-icon size="16">{{ selectedLead.channelIcon }}</v-icon>
            {{ selectedLead.channel }}
          </span>
        </div>

        <section class="drawer-panel">
          <h3>Precalificación BANT</h3>
          <div class="qualification-list compact">
            <article v-for="item in selectedLead.qualification" :key="item.label">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </article>
          </div>

          <div class="next-step">
            <span>Siguiente acción recomendada</span>
            <strong>{{ selectedLead.nextStep }}</strong>
          </div>
        </section>

        <section class="drawer-panel chat-panel">
          <div class="chat-heading">
            <div>
              <h3>Conversación</h3>
              <p>{{ selectedLead.channel }} · Workflow de precalificación</p>
            </div>
            <v-icon size="22">mdi-whatsapp</v-icon>
          </div>

          <div class="whatsapp-chat">
            <div class="chat-date">Historial</div>

            <v-progress-linear
              v-if="loadingLeadHistory"
              indeterminate
              color="primary"
              class="history-progress"
            />

            <v-alert
              v-if="leadHistoryError"
              dense
              outlined
              type="error"
              class="history-alert"
            >
              {{ leadHistoryError }}
              <button type="button" class="alert-action" @click="getLeadHistory()">
                Reintentar
              </button>
            </v-alert>

            <template v-if="selectedLead.conversations.length">
              <article
                v-for="message in selectedLead.conversations"
                :key="message.id || message.text"
                class="chat-message"
                :class="message.type"
              >
                <div v-if="message.type === 'system'" class="chat-system-line">
                  <span></span>
                  <strong>
                    {{ message.title || message.text }}
                    <small
                      v-if="message.title && message.text !== message.title"
                    >
                      {{ message.text }}
                    </small>
                  </strong>
                  <span></span>
                </div>

                <div v-else class="chat-bubble">
                  <strong>{{ message.sender }}</strong>
                  <b v-if="message.title">{{ message.title }}</b>
                  <p>{{ message.text }}</p>
                  <small>
                    {{ message.date }}
                    <v-icon v-if="message.type === 'outgoing'" size="14">mdi-check-all</v-icon>
                  </small>
                </div>
              </article>
            </template>

            <div
              v-else-if="!loadingLeadHistory && !leadHistoryError"
              class="chat-empty-state"
            >
              Este lead todavía no tiene conversaciones o actividades.
            </div>

            <v-btn
              v-if="leadHistoryPagination.has_more"
              text
              class="history-more"
              :loading="loadingLeadHistory"
              @click="getLeadHistory(true)"
            >
              Cargar historial anterior
            </v-btn>

            <div class="chat-composer">
              <button type="button">
                <v-icon size="18">mdi-send-outline</v-icon>
                Enviar
              </button>
            </div>
          </div>
        </section>

        <footer class="drawer-actions">
          <v-btn
            outlined
            class="details-button danger-button"
            @click="openDeleteLeadDialog(selectedLead)"
          >
            <v-icon left size="18">mdi-trash-can-outline</v-icon>
            Eliminar lead
          </v-btn>
          <v-btn outlined class="details-button">
            <v-icon left size="18">mdi-email-outline</v-icon>
            Email
          </v-btn>
          <v-btn outlined class="details-button">
            <v-icon left size="18">mdi-whatsapp</v-icon>
            WhatsApp
          </v-btn>
          <v-btn class="primary-action small" elevation="0">
            Enviar a ventas
          </v-btn>
        </footer>
      </div>
    </v-navigation-drawer>

    <section v-if="activeTab === 'workflows'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Workflows comerciales</h2>
          <p>Configura cómo el agente debe contactar, precalificar y escalar leads.</p>
        </div>
        <v-btn class="secondary-action" elevation="0">
          <v-icon left size="18">mdi-plus</v-icon>
          Nuevo workflow
        </v-btn>
      </div>

      <div class="workflow-list">
        <article v-for="workflow in workflows" :key="workflow.name" class="workflow-row">
          <div>
            <h3>
              {{ workflow.name }}
              <span class="status-pill" :class="workflow.active ? 'success' : 'neutral'">
                {{ workflow.active ? 'Activo' : 'Pausado' }}
              </span>
            </h3>
            <p>{{ workflow.trigger }}</p>
            <div class="workflow-channels">
              <span v-for="channel in workflow.channels" :key="channel">
                {{ channel }}
              </span>
            </div>
          </div>
          <v-btn outlined class="details-button">Configurar</v-btn>
        </article>
      </div>
    </section>

    <section v-if="activeTab === 'history'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Historial del agente</h2>
          <p>Consulta cargas, contactos, calificaciones y oportunidades creadas.</p>
        </div>
      </div>

      <div class="timeline-list">
        <article v-for="event in history" :key="event.title" class="timeline-item">
          <span class="timeline-dot"></span>
          <div>
            <strong>{{ event.title }}</strong>
            <p>{{ event.description }}</p>
            <small>{{ event.date }}</small>
          </div>
        </article>
      </div>
    </section>

    <section v-if="activeTab === 'templates'" class="content-card">
      <div class="card-heading">
        <div>
          <h2>Template de leads</h2>
          <p>
            Puedes cargar tu propio archivo; Fresher AI intentará detectar
            nombres, empresas, contacto, perfil comercial y notas relevantes.
          </p>
        </div>
        <v-btn class="primary-action small" elevation="0" @click="downloadTemplate">
          <v-icon left size="18">mdi-download-outline</v-icon>
          Descargar ejemplo
        </v-btn>
      </div>

      <div class="template-grid">
        <article class="template-card">
          <h3>Columnas recomendadas</h3>
          <div class="columns-grid">
            <span v-for="column in templateColumns" :key="column">
              {{ column }}
            </span>
          </div>
        </article>

        <article class="template-card">
          <h3>Cómo precalifica el agente</h3>
          <ul>
            <li>Detecta presupuesto, autoridad, necesidad y tiempo de decisión.</li>
            <li>Usa metadata para entender empresa, rol, perfil comercial y notas.</li>
            <li>Hace seguimiento automático si no hay respuesta.</li>
            <li>Escala leads calientes al equipo comercial con resumen de contexto.</li>
          </ul>
        </article>
      </div>
    </section>

    <v-dialog v-model="uploadDialog" max-width="880">
      <v-card class="upload-dialog">
        <v-card-title>
          <div>
            <h2>Crear lead</h2>
            <p>
              Registra un prospecto individual o carga varios leads para que el
              agente los asigne a workflow y empiece la precalificación.
            </p>
          </div>
        </v-card-title>

        <v-card-text>
          <div class="mode-switch">
            <button
              type="button"
              :class="{ active: leadFormMode === 'single' }"
              @click="leadFormMode = 'single'"
            >
              Lead individual
            </button>
            <button
              type="button"
              :class="{ active: leadFormMode === 'bulk' }"
              @click="leadFormMode = 'bulk'"
            >
              Carga masiva
            </button>
          </div>

          <div v-if="leadFormMode === 'single'" class="single-lead-form">
            <div class="form-grid two-columns">
              <label>
                <span>Nombre</span>
                <input v-model="leadForm.first_name" type="text" placeholder="Ana" />
              </label>

              <label>
                <span>Apellido</span>
                <input v-model="leadForm.last_name" type="text" placeholder="Rodriguez" />
              </label>

              <label>
                <span>Email</span>
                <input v-model="leadForm.email" type="email" placeholder="ana@empresa.com" />
              </label>

              <label>
                <span>Telefono</span>
                <input v-model="leadForm.phone" type="tel" placeholder="+52 55 0000 0000" />
              </label>
            </div>

            <div class="metadata-panel">
              <h3>Metadata opcional</h3>
              <div class="form-grid two-columns">
                <label>
                  <span>Tipo de lead</span>
                  <select v-model="leadForm.metadata.lead_type">
                    <option value="person">Persona</option>
                    <option value="company">Empresa</option>
                    <option value="company_contact">Contacto en empresa</option>
                  </select>
                </label>

                <label>
                  <span>Empresa</span>
                  <input
                    v-model="leadForm.metadata.company_name"
                    type="text"
                    placeholder="Comercial del Norte"
                  />
                </label>

                <label>
                  <span>Puesto / rol</span>
                  <input v-model="leadForm.metadata.role" type="text" placeholder="Compras" />
                </label>

                <label>
                  <span>Perfil comercial</span>
                  <input
                    v-model="leadForm.metadata.profile"
                    type="text"
                    placeholder="Exportador de aguacate"
                  />
                </label>

                <label>
                  <span>Canal preferido</span>
                  <select v-model="leadForm.metadata.preferred_channel">
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                    <option value="phone">Llamada</option>
                  </select>
                </label>

                <label>
                  <span>Fuente</span>
                  <input v-model="leadForm.metadata.source" type="text" placeholder="Manual" />
                </label>
              </div>

              <label class="full-field">
                <span>Notas para el agente</span>
                <textarea
                  v-model="leadForm.metadata.notes"
                  rows="4"
                  placeholder="Ej. Cliente referido, posible exportador de aguacate, prefiere WhatsApp."
                ></textarea>
              </label>

              <div class="metadata-panel">
                <h3>Forzar primer mensaje (opcional)</h3>
                <div class="form-grid two-columns">
                  <label>
                    <span>Canal del primer mensaje</span>
                    <select v-model="leadForm.forceFirstMessage.channel">
                      <option value="">Usar cadencia normal del workflow</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="email">Email</option>
                    </select>
                  </label>
                </div>

                <label v-if="leadForm.forceFirstMessage.channel === 'email'" class="full-field">
                  <span>Asunto del email</span>
                  <input
                    v-model="leadForm.forceFirstMessage.subject"
                    type="text"
                    placeholder="Ej. Bienvenido a FresherPay"
                  />
                </label>

                <label class="full-field">
                  <span>Contenido del mensaje</span>
                  <textarea
                    v-model="leadForm.forceFirstMessage.content"
                    rows="3"
                    :disabled="!leadForm.forceFirstMessage.channel"
                    placeholder="Ej. Hola, gracias por tu interés en FresherPay..."
                  ></textarea>
                </label>
              </div>
            </div>
          </div>

          <div v-else class="bulk-lead-form">
            <div class="upload-grid">
              <button type="button" class="dropzone" @click="$refs.leadsFile.click()">
                <v-icon color="#0F82C3" size="38">mdi-cloud-upload-outline</v-icon>
                <strong>{{ selectedFileName || 'Seleccionar archivo' }}</strong>
                <span>Formato: .xlsx (descarga el template)</span>
              </button>
              <input
                ref="leadsFile"
                type="file"
                accept=".xlsx"
                class="hidden-input"
                @change="handleFileChange"
              />

              <div class="upload-options">
                <label>
                  <span>Tipo de carga</span>
                  <select v-model="uploadForm.listType">
                    <option value="people">Personas</option>
                    <option value="companies">Empresas</option>
                    <option value="mixed">Mixta</option>
                  </select>
                </label>

                <label>
                  <span>Metadata general</span>
                  <textarea
                    v-model="uploadForm.defaultNotes"
                    rows="5"
                    placeholder="Ej. Leads de expo produce, perfil: exportadores de aguacate, prioridad alta."
                  ></textarea>
                </label>

                <label class="checkbox-row">
                  <input style="width: 15px;" v-model="uploadForm.autoFormat" type="checkbox" />
                  <span>Permitir que IA formatee columnas automáticamente</span>
                </label>
              </div>
            </div>

            <div class="mapping-preview">
              <h3>Vista previa esperada</h3>
              <div class="preview-table">
                <div v-for="row in previewRows" :key="row.email" class="preview-row">
                  <span>{{ row.name }}</span>
                  <span>{{ row.company }}</span>
                  <span>{{ row.profile }}</span>
                  <span>{{ row.channel }}</span>
                  <span>{{ row.status }}</span>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn text @click="uploadDialog = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn outlined class="details-button" @click="downloadTemplate">
            Descargar template
          </v-btn>
          <v-btn
            class="primary-action small"
            elevation="0"
            :loading="creatingLead || importingLeads"
            @click="submitLeadForm"
          >
            {{ leadFormMode === 'single' ? 'Crear lead' : 'Procesar carga masiva' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteLeadDialog" max-width="440" content-class="delete-lead-dialog">
      <v-card class="confirm-dialog">
        <v-card-title>Eliminar lead</v-card-title>
        <v-card-text>
          ¿Eliminar a <strong>{{ leadPendingDeletion && leadPendingDeletion.name }}</strong>? Esta acción no se puede deshacer.
          Se detendrá el contacto automático y se borrará de FresherPay.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteLeadDialog = false">Cancelar</v-btn>
          <v-btn
            class="danger-action"
            elevation="0"
            :loading="deletingLead"
            @click="deleteLead"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </main>
</template>

<script>
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

export default {
  name: 'LeadsAgent',
  data() {
    return {
      activeTab: 'leads',
      loadingLeads: false,
      syncingLeads: false,
      creatingLead: false,
      importingLeads: false,
      deletingLead: false,
      deleteLeadDialog: false,
      leadPendingDeletion: null,
      loadingLeadHistory: false,
      leadsError: '',
      leadHistoryError: '',
      lastSyncedAt: null,
      leadHistoryPagination: {
        limit: 50,
        offset: 0,
        has_more: false,
      },
      leadPage: 1,
      leadPagination: {
        page: 0,
        limit: 25,
        total: 0,
        has_more: false,
      },
      leadSummary: {
        total: 0,
        status_counts: {},
        qualification_counts: {},
      },
      searchTimer: null,
      uploadDialog: false,
      leadDrawer: false,
      selectedLead: null,
      selectedFile: null,
      selectedFileName: '',
      leadViewMode: 'table',
      leadFilters: {
        search: '',
        status: 'all',
        channel: 'all',
      },
      leadStatuses: [
        { label: 'Nuevo', value: 'new' },
        { label: 'Contactando', value: 'contacting' },
        { label: 'Contactado', value: 'contacted' },
        { label: 'Interesado', value: 'engaged' },
        { label: 'Precalificando', value: 'qualifying' },
        { label: 'Listo para ventas', value: 'qualified' },
        { label: 'Seguimiento', value: 'follow_up' },
        { label: 'Descartado', value: 'disqualified' },
      ],
      pipelineStages: [
        { label: 'Nuevo', value: 'new' },
        { label: 'Contactando', value: 'contacting' },
        { label: 'Respondió', value: 'engaged' },
        { label: 'Precalificando', value: 'qualifying' },
        { label: 'Listo para ventas', value: 'qualified' },
        { label: 'Seguimiento', value: 'follow_up' },
      ],
      leadFormMode: 'single',
      leadForm: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        workflow_id: '',
        campaign_id: '',
        forceFirstMessage: {
          channel: '',
          content: '',
          subject: '',
        },
        metadata: {
          lead_type: 'company_contact',
          company_name: '',
          role: '',
          profile: '',
          preferred_channel: 'whatsapp',
          source: 'manual',
          notes: '',
        },
      },
      uploadForm: {
        listType: 'companies',
        workflow_id: '',
        campaign_id: '',
        defaultNotes: '',
        autoFormat: true,
      },
      tabs: [
        { label: 'Leads', value: 'leads' },
        { label: 'Workflows', value: 'workflows', disabled: true },
        { label: 'Historial', value: 'history' },
        { label: 'Templates', value: 'templates', disabled: true },
      ],
      mockMetrics: [
        {
          label: 'Leads cargados',
          value: '1,240',
          helper: 'En campañas activas',
          icon: 'mdi-account-plus-outline',
          color: '#246BFE',
        },
        {
          label: 'Precalificados',
          value: '312',
          helper: '+18% vs mes anterior',
          icon: 'mdi-filter-check-outline',
          color: '#16A34A',
        },
        {
          label: 'Oportunidades',
          value: '86',
          helper: 'Listas para ventas',
          icon: 'mdi-briefcase-check-outline',
          color: '#7C3AED',
        },
        {
          label: 'Tasa de respuesta',
          value: '42%',
          helper: 'Promedio multicanal',
          icon: 'mdi-message-reply-text-outline',
          color: '#FF5A1F',
        },
      ],
      leads: [],
      mockLeads: [
        {
          id: 1,
          name: 'Ana Rodriguez',
          company: 'Comercial del Norte',
          product: 'Precalificación BANT',
          profile: 'Compradora mayorista',
          channel: 'WhatsApp',
          channelIcon: 'mdi-whatsapp',
          lastContact: 'Hace 2 horas',
          status: 'Interesado',
          statusClass: 'progress',
          bantProgress: 'BANT 3/4',
          nextStep: 'Validar autoridad de compra y pasar contexto al representante comercial.',
          qualification: [
            { label: 'Budget', value: 'Tiene presupuesto operativo, monto por validar' },
            { label: 'Authority', value: 'Participa en compras, requiere visto bueno de dirección' },
            { label: 'Need', value: 'Busca mejorar seguimiento comercial y reducir tareas manuales' },
            { label: 'Timeline', value: 'Quiere revisar opciones esta semana' },
          ],
          conversations: [
            {
              type: 'incoming',
              sender: 'Ana Rodriguez',
              text: 'Hola, sí me interesa entender cómo funcionaría para mi equipo. Hoy damos seguimiento manual a muchos prospectos.',
              date: 'Hace 2 horas',
            },
            {
              type: 'system',
              text: 'Ana Rodriguez pasó de Nuevo a Interesado',
            },
            {
              type: 'outgoing',
              sender: 'Agente Fresher',
              text: 'Perfecto, Ana. Para saber si vale la pena pasarte con ventas: ¿ustedes ya tienen presupuesto asignado para herramientas de prospección este mes?',
              date: 'Hace 1 hora',
            },
            {
              type: 'incoming',
              sender: 'Ana Rodriguez',
              text: 'Sí hay presupuesto, pero lo tendría que autorizar dirección si vemos que nos ahorra tiempo.',
              date: 'Hace 48 min',
            },
          ],
        },
        {
          id: 2,
          name: 'Carlos Medina',
          company: 'Importadora Río Bravo',
          product: 'Precalificación BANT',
          profile: 'Importador produce',
          channel: 'Email',
          channelIcon: 'mdi-email-outline',
          lastContact: 'Hace 1 día',
          status: 'Precalificando',
          statusClass: 'neutral',
          bantProgress: 'BANT 1/4',
          nextStep: 'Esperar respuesta y hacer segundo touchpoint del workflow.',
          qualification: [
            { label: 'Budget', value: 'No detectado' },
            { label: 'Authority', value: 'Por confirmar' },
            { label: 'Need', value: 'Quiere entender casos de uso antes de avanzar' },
            { label: 'Timeline', value: 'Sin fecha clara' },
          ],
          conversations: [
            {
              type: 'outgoing',
              sender: 'Agente Fresher',
              text: 'Hola Carlos, soy el asistente de Fresher. ¿Te puedo hacer 3 preguntas rápidas para validar si tiene sentido que un representante te contacte?',
              date: 'Hace 1 día',
            },
            {
              type: 'incoming',
              sender: 'Carlos Medina',
              text: 'Sí, mándame la información por aquí y la reviso.',
              date: 'Hace 23 horas',
            },
            {
              type: 'system',
              text: 'Workflow programó segundo touchpoint',
            },
          ],
        },
        {
          id: 3,
          name: 'Sofía Jiménez',
          company: 'RetailTech Bajío',
          product: 'Precalificación BANT',
          profile: 'Retail / compras',
          channel: 'Llamada',
          channelIcon: 'mdi-phone-outline',
          lastContact: 'Hace 3 días',
          status: 'Demo solicitada',
          statusClass: 'success',
          bantProgress: 'BANT 4/4',
          nextStep: 'Enviar resumen a ventas y sugerir llamada de descubrimiento.',
          qualification: [
            { label: 'Budget', value: 'Tiene presupuesto activo' },
            { label: 'Authority', value: 'Dueña / compras' },
            { label: 'Need', value: 'Quiere automatizar prospección' },
            { label: 'Timeline', value: 'Inmediata' },
          ],
          conversations: [
            {
              type: 'incoming',
              sender: 'Sofía Jiménez',
              text: 'Nos interesa porque hoy una persona del equipo pasa mucho tiempo escribiendo a prospectos.',
              date: 'Hace 3 días',
            },
            {
              type: 'outgoing',
              sender: 'Agente Fresher',
              text: 'Gracias, Sofía. ¿Tú decides la contratación o alguien más debe aprobarla?',
              date: 'Hace 3 días',
            },
            {
              type: 'incoming',
              sender: 'Sofía Jiménez',
              text: 'Yo lo puedo aprobar si la demo se ve útil. Me gustaría agendarla.',
              date: 'Hace 3 días',
            },
            {
              type: 'system',
              text: 'Lead listo para handoff comercial',
            },
          ],
        },
        {
          id: 4,
          name: 'Miguel Herrera',
          company: 'Logística Punto Sur',
          product: 'Precalificación BANT',
          profile: 'Operador logistico',
          channel: 'WhatsApp',
          channelIcon: 'mdi-whatsapp',
          lastContact: 'Sin respuesta 4 días',
          status: 'Seguimiento',
          statusClass: 'progress',
          bantProgress: 'BANT 0/4',
          nextStep: 'Enviar seguimiento corto para confirmar si tiene necesidad activa.',
          qualification: [
            { label: 'Budget', value: 'No detectado' },
            { label: 'Authority', value: 'Por confirmar' },
            { label: 'Need', value: 'No confirmado' },
            { label: 'Timeline', value: 'Baja urgencia' },
          ],
          conversations: [
            {
              type: 'outgoing',
              sender: 'Agente Fresher',
              text: 'Hola Miguel, soy el asistente de Fresher. ¿Actualmente tu equipo hace prospección manual o ya usan alguna herramienta?',
              date: 'Hace 4 días',
            },
            {
              type: 'system',
              text: 'Sin respuesta: seguimiento programado',
            },
          ],
        },
        {
          id: 1,
          name: 'Ana Rodriguez',
          company: 'Comercial del Norte',
          product: 'Produce disponible',
          channel: 'WhatsApp',
          channelIcon: 'mdi-whatsapp',
          lastContact: 'Hace 2 horas',
          status: 'Interesado',
          statusClass: 'progress',
          nextStep: 'Confirmar volumen semanal de aguacate y tomate para preparar cotización.',
          qualification: [
            { label: 'Interés', value: 'Aguacate y tomate para compra semanal' },
            { label: 'Volumen', value: '20 a 30 cajas' },
            { label: 'Urgencia', value: 'Entrega esta semana' },
            { label: 'Decisor', value: 'Compras' },
          ],
          conversations: [
            {
              channel: 'WhatsApp',
              icon: 'mdi-whatsapp',
              text: 'Respondió que le interesa precio de aguacate Hass y disponibilidad para jueves.',
              date: 'Hace 2 horas',
            },
            {
              channel: 'Email',
              icon: 'mdi-email-outline',
              text: 'Se envió lista de precios y condiciones de entrega.',
              date: 'Ayer',
            },
          ],
        },
        {
          id: 2,
          name: 'Carlos Medina',
          company: 'Importadora Río Bravo',
          product: 'Lista de precios semanal',
          channel: 'Email',
          channelIcon: 'mdi-email-outline',
          lastContact: 'Hace 1 día',
          status: 'Precalificando',
          statusClass: 'neutral',
          nextStep: 'Solicitar productos de mayor rotación y zona de entrega.',
          qualification: [
            { label: 'Interés', value: 'Fruta de temporada' },
            { label: 'Volumen', value: 'Por confirmar' },
            { label: 'Urgencia', value: 'Próxima semana' },
            { label: 'Decisor', value: 'Por confirmar' },
          ],
          conversations: [
            {
              channel: 'Email',
              icon: 'mdi-email-outline',
              text: 'Abrió correo con lista de precios, sin responder todavía.',
              date: 'Hace 1 día',
            },
          ],
        },
        {
          id: 3,
          name: 'Sofía Jiménez',
          company: 'RetailTech Bajío',
          product: 'Promoción del día',
          channel: 'Llamada',
          channelIcon: 'mdi-phone-outline',
          lastContact: 'Hace 3 días',
          status: 'Demo solicitada',
          statusClass: 'success',
          nextStep: 'Enviar promoción de fresa y confirmar horario de entrega.',
          qualification: [
            { label: 'Interés', value: 'Fresa premium y limón persa' },
            { label: 'Volumen', value: 'Compra recurrente' },
            { label: 'Urgencia', value: 'Inmediata' },
            { label: 'Decisor', value: 'Dueña / compras' },
          ],
          conversations: [
            {
              channel: 'Llamada',
              icon: 'mdi-phone-outline',
              text: 'Pidió precio final por volumen y disponibilidad de entrega.',
              date: 'Hace 3 días',
            },
          ],
        },
        {
          id: 4,
          name: 'Miguel Herrera',
          company: 'Logística Punto Sur',
          product: 'Produce disponible',
          channel: 'WhatsApp',
          channelIcon: 'mdi-whatsapp',
          lastContact: 'Sin respuesta 4 días',
          status: 'Seguimiento',
          statusClass: 'progress',
          nextStep: 'Enviar mensaje corto con 3 productos destacados y preguntar volumen.',
          qualification: [
            { label: 'Interés', value: 'No confirmado' },
            { label: 'Volumen', value: 'Desconocido' },
            { label: 'Urgencia', value: 'Baja' },
            { label: 'Decisor', value: 'Por confirmar' },
          ],
          conversations: [
            {
              channel: 'WhatsApp',
              icon: 'mdi-whatsapp',
              text: 'Primer contacto enviado. Pendiente respuesta.',
              date: 'Hace 4 días',
            },
          ],
        },
        {
          id: 1,
          name: 'Ana Rodriguez',
          company: 'Comercial del Norte',
          product: 'Produce disponible',
          channel: 'WhatsApp',
          channelIcon: 'mdi-whatsapp',
          lastContact: 'Hace 2 horas',
          status: 'Interesado',
          statusClass: 'progress',
          nextStep: 'Confirmar volumen semanal de aguacate y tomate para preparar cotización.',
          qualification: [
            { label: 'Interés', value: 'Aguacate y tomate para compra semanal' },
            { label: 'Volumen', value: '20 a 30 cajas' },
            { label: 'Urgencia', value: 'Entrega esta semana' },
            { label: 'Decisor', value: 'Compras' },
          ],
          conversations: [
            {
              channel: 'WhatsApp',
              icon: 'mdi-whatsapp',
              text: 'Respondió que le interesa precio de aguacate Hass y disponibilidad para jueves.',
              date: 'Hace 2 horas',
            },
            {
              channel: 'Email',
              icon: 'mdi-email-outline',
              text: 'Se envió lista de precios y condiciones de entrega.',
              date: 'Ayer',
            },
          ],
        },
        {
          id: 2,
          name: 'Carlos Medina',
          company: 'Importadora Río Bravo',
          product: 'Lista de precios semanal',
          channel: 'Email',
          channelIcon: 'mdi-email-outline',
          lastContact: 'Hace 1 día',
          status: 'Precalificando',
          statusClass: 'neutral',
          nextStep: 'Solicitar productos de mayor rotación y zona de entrega.',
          qualification: [
            { label: 'Interés', value: 'Fruta de temporada' },
            { label: 'Volumen', value: 'Por confirmar' },
            { label: 'Urgencia', value: 'Próxima semana' },
            { label: 'Decisor', value: 'Por confirmar' },
          ],
          conversations: [
            {
              channel: 'Email',
              icon: 'mdi-email-outline',
              text: 'Abrió correo con lista de precios, sin responder todavía.',
              date: 'Hace 1 día',
            },
          ],
        },
        {
          id: 3,
          name: 'Sofía Jiménez',
          company: 'RetailTech Bajío',
          product: 'Promoción del día',
          channel: 'Llamada',
          channelIcon: 'mdi-phone-outline',
          lastContact: 'Hace 3 días',
          status: 'Demo solicitada',
          statusClass: 'success',
          nextStep: 'Enviar promoción de fresa y confirmar horario de entrega.',
          qualification: [
            { label: 'Interés', value: 'Fresa premium y limón persa' },
            { label: 'Volumen', value: 'Compra recurrente' },
            { label: 'Urgencia', value: 'Inmediata' },
            { label: 'Decisor', value: 'Dueña / compras' },
          ],
          conversations: [
            {
              channel: 'Llamada',
              icon: 'mdi-phone-outline',
              text: 'Pidió precio final por volumen y disponibilidad de entrega.',
              date: 'Hace 3 días',
            },
          ],
        },
        {
          id: 4,
          name: 'Miguel Herrera',
          company: 'Logística Punto Sur',
          product: 'Produce disponible',
          channel: 'WhatsApp',
          channelIcon: 'mdi-whatsapp',
          lastContact: 'Sin respuesta 4 días',
          status: 'Seguimiento',
          statusClass: 'progress',
          nextStep: 'Enviar mensaje corto con 3 productos destacados y preguntar volumen.',
          qualification: [
            { label: 'Interés', value: 'No confirmado' },
            { label: 'Volumen', value: 'Desconocido' },
            { label: 'Urgencia', value: 'Baja' },
            { label: 'Decisor', value: 'Por confirmar' },
          ],
          conversations: [
            {
              channel: 'WhatsApp',
              icon: 'mdi-whatsapp',
              text: 'Primer contacto enviado. Pendiente respuesta.',
              date: 'Hace 4 días',
            },
          ],
        },
        {
          id: 1,
          name: 'Ana Rodriguez',
          company: 'Comercial del Norte',
          product: 'Produce disponible',
          channel: 'WhatsApp',
          channelIcon: 'mdi-whatsapp',
          lastContact: 'Hace 2 horas',
          status: 'Interesado',
          statusClass: 'progress',
          nextStep: 'Confirmar volumen semanal de aguacate y tomate para preparar cotización.',
          qualification: [
            { label: 'Interés', value: 'Aguacate y tomate para compra semanal' },
            { label: 'Volumen', value: '20 a 30 cajas' },
            { label: 'Urgencia', value: 'Entrega esta semana' },
            { label: 'Decisor', value: 'Compras' },
          ],
          conversations: [
            {
              channel: 'WhatsApp',
              icon: 'mdi-whatsapp',
              text: 'Respondió que le interesa precio de aguacate Hass y disponibilidad para jueves.',
              date: 'Hace 2 horas',
            },
            {
              channel: 'Email',
              icon: 'mdi-email-outline',
              text: 'Se envió lista de precios y condiciones de entrega.',
              date: 'Ayer',
            },
          ],
        },
        {
          id: 2,
          name: 'Carlos Medina',
          company: 'Importadora Río Bravo',
          product: 'Lista de precios semanal',
          channel: 'Email',
          channelIcon: 'mdi-email-outline',
          lastContact: 'Hace 1 día',
          status: 'Precalificando',
          statusClass: 'neutral',
          nextStep: 'Solicitar productos de mayor rotación y zona de entrega.',
          qualification: [
            { label: 'Interés', value: 'Fruta de temporada' },
            { label: 'Volumen', value: 'Por confirmar' },
            { label: 'Urgencia', value: 'Próxima semana' },
            { label: 'Decisor', value: 'Por confirmar' },
          ],
          conversations: [
            {
              channel: 'Email',
              icon: 'mdi-email-outline',
              text: 'Abrió correo con lista de precios, sin responder todavía.',
              date: 'Hace 1 día',
            },
          ],
        },
        {
          id: 3,
          name: 'Sofía Jiménez',
          company: 'RetailTech Bajío',
          product: 'Promoción del día',
          channel: 'Llamada',
          channelIcon: 'mdi-phone-outline',
          lastContact: 'Hace 3 días',
          status: 'Demo solicitada',
          statusClass: 'success',
          nextStep: 'Enviar promoción de fresa y confirmar horario de entrega.',
          qualification: [
            { label: 'Interés', value: 'Fresa premium y limón persa' },
            { label: 'Volumen', value: 'Compra recurrente' },
            { label: 'Urgencia', value: 'Inmediata' },
            { label: 'Decisor', value: 'Dueña / compras' },
          ],
          conversations: [
            {
              channel: 'Llamada',
              icon: 'mdi-phone-outline',
              text: 'Pidió precio final por volumen y disponibilidad de entrega.',
              date: 'Hace 3 días',
            },
          ],
        },
        {
          id: 4,
          name: 'Miguel Herrera',
          company: 'Logística Punto Sur',
          product: 'Produce disponible',
          channel: 'WhatsApp',
          channelIcon: 'mdi-whatsapp',
          lastContact: 'Sin respuesta 4 días',
          status: 'Seguimiento',
          statusClass: 'progress',
          nextStep: 'Enviar mensaje corto con 3 productos destacados y preguntar volumen.',
          qualification: [
            { label: 'Interés', value: 'No confirmado' },
            { label: 'Volumen', value: 'Desconocido' },
            { label: 'Urgencia', value: 'Baja' },
            { label: 'Decisor', value: 'Por confirmar' },
          ],
          conversations: [
            {
              channel: 'WhatsApp',
              icon: 'mdi-whatsapp',
              text: 'Primer contacto enviado. Pendiente respuesta.',
              date: 'Hace 4 días',
            },
          ],
        },
      ],
      workflows: [
        {
          name: 'Precalificación BANT',
          trigger: 'Trigger: lead responde o muestra intención comercial',
          active: true,
          channels: ['WhatsApp', 'Llamada'],
        }
      ],
      history: [
        {
          title: 'Lista de leads procesada',
          description: 'Se cargaron 320 prospectos, 280 empresas y 40 personas.',
          date: 'Hoy 10:15',
        },
        {
          title: 'Oportunidad creada',
          description: 'RetailTech Bajío fue marcado como listo para handoff comercial.',
          date: 'Ayer 17:30',
        },
        {
          title: 'Workflow de seguimiento ejecutado',
          description: 'Se enviaron 62 mensajes a leads sin respuesta.',
          date: '07 Jun 2026',
        },
      ],
      templateColumns: [
        'first_name',
        'last_name',
        'email',
        'phone',
        'workflow_id',
        'campaign_id',
        'metadata.lead_type',
        'metadata.company_name',
        'metadata.role',
        'metadata.profile',
        'metadata.preferred_channel',
        'metadata.source',
        'metadata.notes',
      ],
      previewRows: [
        {
          name: 'Ana Rodriguez',
          company: 'Comercial del Norte',
          profile: 'Exportador de aguacate',
          channel: 'WhatsApp',
          status: 'Nuevo',
          email: 'ana@comercialnorte.com',
        },
        {
          name: 'Carlos Medina',
          company: 'Importadora Río Bravo',
          profile: 'Importador produce',
          channel: 'Email',
          status: 'Nuevo',
          email: 'carlos@riobravo.com',
        },
      ],
    }
  },
  computed: {
    metrics() {
      const statusCounts = this.leadSummary.status_counts || {}
      const qualificationCounts = this.leadSummary.qualification_counts || {}
      const responded = [
        'engaged',
        'qualifying',
        'qualified',
        'follow_up',
      ].reduce((total, status) => total + Number(statusCounts[status] || 0), 0)

      return [
        {
          label: 'Leads registrados',
          value: Number(this.leadSummary.total || 0).toLocaleString('es-MX'),
          helper: 'En tu base de prospección',
          icon: 'mdi-account-plus-outline',
          color: '#246BFE',
        },
        {
          label: 'Precalificados',
          value: Number(qualificationCounts.qualified || 0).toLocaleString('es-MX'),
          helper: 'Calificación completada',
          icon: 'mdi-filter-check-outline',
          color: '#16A34A',
        },
        {
          label: 'Listos para ventas',
          value: Number(statusCounts.qualified || 0).toLocaleString('es-MX'),
          helper: 'Preparados para handoff',
          icon: 'mdi-briefcase-check-outline',
          color: '#7C3AED',
        },
        {
          label: 'Respondieron',
          value: responded.toLocaleString('es-MX'),
          helper: 'Con interacción detectada',
          icon: 'mdi-message-reply-text-outline',
          color: '#FF5A1F',
        },
      ]
    },
    leadPageCount() {
      return Math.max(
        1,
        Math.ceil(this.leadPagination.total / this.leadPagination.limit)
      )
    },
    visibleLeads() {
      const seen = new Set()
      return this.leads.filter((lead) => {
        if (seen.has(lead.id)) return false
        seen.add(lead.id)
        return true
      })
    },
    filteredLeads() {
      const search = this.leadFilters.search.trim().toLowerCase()

      return this.visibleLeads.filter((lead) => {
        const matchesSearch = !search || [
          lead.name,
          lead.company,
          lead.channel,
          lead.profile,
          lead.status,
        ].some((value) => String(value || '').toLowerCase().includes(search))

        const matchesStatus = this.leadFilters.status === 'all'
          || lead.statusKey === this.leadFilters.status
        const matchesChannel = this.leadFilters.channel === 'all'
          || lead.channel === this.leadFilters.channel

        return matchesSearch && matchesStatus && matchesChannel
      })
    },
  },
  watch: {
    leadPage() {
      this.getLeads()
    },
    'leadFilters.status'() {
      this.resetLeadPageAndLoad()
    },
    'leadFilters.channel'() {
      this.resetLeadPageAndLoad()
    },
    'leadFilters.search'() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.resetLeadPageAndLoad()
      }, 350)
    },
  },
  mounted() {
    this.getLeads({ triggerSync: true })

    // El backend dispara la sincronización con Nexor al pedir la lista con
    // ?sync=true (fire-and-forget). Refrescamos una vez unos segundos después
    // para ver los cambios sin recargar de nuevo.
    this._postSyncRefresh = setTimeout(() => {
      this.getLeads({ triggerSync: false })
    }, 6000)
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
    clearTimeout(this._postSyncRefresh)
  },
  methods: {
    getRequestConfig(params) {
      return {
        params,
        headers: {
          Authorization: this.$store.state.sToken
            ? `Bearer ${this.$store.state.sToken}`
            : '',
        },
      }
    },
    resetLeadPageAndLoad() {
      if (this.leadPage !== 1) {
        this.leadPage = 1
        return
      }

      this.getLeads()
    },
    async getLeads({ triggerSync = false } = {}) {
      this.loadingLeads = true
      this.leadsError = ''

      try {
        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/leads/listLeads`,
          this.getRequestConfig({
            agent_type: 'sales',
            page: this.leadPage - 1,
            limit: this.leadPagination.limit,
            search: this.leadFilters.search || undefined,
            status: this.leadFilters.status === 'all'
              ? undefined
              : this.leadFilters.status,
            channel: this.leadFilters.channel === 'all'
              ? undefined
              : this.leadFilters.channel.toLowerCase(),
            sync: triggerSync ? 'true' : undefined,
          })
        )
        const payload = response.data?.data || {}

        this.leads = (payload.leads || []).map(this.mapApiLead)
        this.leadPagination = payload.pagination || this.leadPagination
        this.leadSummary = payload.summary || this.leadSummary
        this.lastSyncedAt = payload.last_synced_at || null
      } catch (error) {
        this.leads = []
        this.leadsError = error.response?.data?.message
          || 'No fue posible cargar los leads.'
        this.mixError(this.leadsError, error.response?.status || 400)
      } finally {
        this.loadingLeads = false
      }
    },
    async syncLeads() {
      this.syncingLeads = true
      this.leadsError = ''

      try {
        const response = await DB.post(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/leads/syncLeads`,
          {
            agent_type: 'sales',
          },
          this.getRequestConfig()
        )
        const summary = response.data?.data || {}

        this.mixSuccess(
          `Sincronización completa: ${summary.created || 0} nuevos, `
          + `${summary.updated || 0} actualizados.`
        )

        if (this.leadPage !== 1) {
          this.leadPage = 1
        } else {
          await this.getLeads()
        }
      } catch (error) {
        this.leadsError = error.response?.data?.message
          || 'No fue posible sincronizar los leads.'
        this.mixError(this.leadsError, error.response?.status || 400)
      } finally {
        this.syncingLeads = false
      }
    },
    openDeleteLeadDialog(lead) {
      if (!lead) return
      this.leadPendingDeletion = lead
      this.deleteLeadDialog = true
    },
    async deleteLead() {
      if (!this.leadPendingDeletion) return

      this.deletingLead = true

      try {
        await DB.delete(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/leads/${this.leadPendingDeletion.id}`,
          this.getRequestConfig()
        )

        this.mixSuccess('Lead eliminado correctamente')
        this.deleteLeadDialog = false
        this.leadPendingDeletion = null
        this.leadDrawer = false
        this.selectedLead = null
        this.getLeads()
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible eliminar el lead.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.deletingLead = false
      }
    },
    mapApiLead(lead) {
      const metadata = lead.metadata || {}
      const statusPresentation = this.getStatusPresentation(lead.status)
      const channel = this.getLeadChannel(metadata)
      const companyValue = metadata.company_name
        || metadata.company?.name
        || metadata.company
        || 'Sin empresa'
      const qualification = ['budget', 'authority', 'need', 'timeline'].map(
        (key) => ({
          label: key.charAt(0).toUpperCase() + key.slice(1),
          value: this.getBantValue(lead.bant_data, key),
        })
      )

      return {
        ...lead,
        name: [lead.first_name, lead.last_name].filter(Boolean).join(' ')
          || 'Lead sin nombre',
        company: typeof companyValue === 'string'
          ? companyValue
          : 'Sin empresa',
        profile: metadata.profile || metadata.role || 'Sin perfil comercial',
        channel: channel.label,
        channelIcon: channel.icon,
        lastContact: this.formatRelativeDate(
          lead.last_contacted_at || lead.last_synced_at
        ),
        status: statusPresentation.label,
        statusKey: statusPresentation.key,
        statusClass: statusPresentation.className,
        bantProgress: `BANT ${Number(lead.bant_completed_count || 0)}/4`,
        nextStep: lead.next_step || 'Esperar la siguiente actualización del agente.',
        qualification,
        conversations: [],
      }
    },
    getStatusPresentation(status) {
      const presentations = {
        draft: { label: 'Borrador', className: 'neutral' },
        new: { label: 'Nuevo', className: 'neutral' },
        contacting: { label: 'Contactando', className: 'progress' },
        contacted: { label: 'Contactado', className: 'progress' },
        engaged: { label: 'Interesado', className: 'progress' },
        qualifying: { label: 'Precalificando', className: 'warning' },
        qualified: { label: 'Listo para ventas', className: 'success' },
        follow_up: { label: 'Seguimiento', className: 'progress' },
        disqualified: { label: 'Descartado', className: 'neutral' },
        archived: { label: 'Archivado', className: 'neutral' },
      }

      return {
        key: status || 'new',
        ...(presentations[status] || {
          label: status || 'Sin estatus',
          className: 'neutral',
        }),
      }
    },
    getLeadChannel(metadata) {
      const rawChannel = String(
        metadata.preferred_channel || metadata.source || ''
      ).toLowerCase()

      if (rawChannel.includes('whatsapp')) {
        return { label: 'WhatsApp', icon: 'mdi-whatsapp' }
      }
      if (rawChannel.includes('email')) {
        return { label: 'Email', icon: 'mdi-email-outline' }
      }
      if (rawChannel.includes('phone') || rawChannel.includes('call')) {
        return { label: 'Llamada', icon: 'mdi-phone-outline' }
      }

      return { label: 'Sin canal', icon: 'mdi-message-outline' }
    },
    getBantValue(bantData, key) {
      const value = bantData?.[key]

      if (value && typeof value === 'object') {
        return value.value || value.answer || value.notes || 'No detectado'
      }

      return value || 'No detectado'
    },
    formatRelativeDate(value) {
      if (!value) return 'Sin interacción'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'Sin interacción'

      const diffMinutes = Math.max(
        0,
        Math.floor((Date.now() - date.getTime()) / 60000)
      )
      if (diffMinutes < 60) return `Hace ${diffMinutes || 1} min`
      if (diffMinutes < 1440) return `Hace ${Math.floor(diffMinutes / 60)} h`
      return `Hace ${Math.floor(diffMinutes / 1440)} días`
    },
    formatDateTime(value) {
      if (!value) return ''

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''

      return new Intl.DateTimeFormat('es-MX', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date)
    },
    async openLead(lead) {
      this.selectedLead = {
        ...lead,
        conversations: [],
      }
      this.leadDrawer = true
      this.leadHistoryError = ''
      this.leadHistoryPagination = {
        limit: 50,
        offset: 0,
        has_more: false,
      }
      await this.getLeadHistory()
    },
    applySyncedLead(row) {
      if (!row || !row.id) return
      const mapped = this.mapApiLead(row)
      const idx = this.leads.findIndex((l) => l.id === mapped.id)
      if (idx !== -1) {
        this.$set(this.leads, idx, { ...this.leads[idx], ...mapped, conversations: this.leads[idx].conversations })
      }
      if (this.selectedLead && this.selectedLead.id === mapped.id) {
        this.selectedLead = {
          ...this.selectedLead,
          ...mapped,
          conversations: this.selectedLead.conversations,
        }
      }
    },
    async getLeadHistory(append = false) {
      if (!this.selectedLead?.id) return

      const selectedLeadId = this.selectedLead.id
      const offset = append
        ? this.selectedLead.conversations.length
        : 0

      this.loadingLeadHistory = true
      this.leadHistoryError = ''

      try {
        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/leads/${selectedLeadId}/history`,
          this.getRequestConfig({
            limit: this.leadHistoryPagination.limit,
            offset,
          })
        )
        const payload = response.data?.data || {}

        if (this.selectedLead?.id !== selectedLeadId) return

        // El backend sincroniza este lead con Nexor al abrir el detalle y
        // devuelve la fila actualizada: la reflejamos en la tabla y el drawer.
        if (payload.synced_lead) {
          this.applySyncedLead(payload.synced_lead)
        }

        const history = (payload.history || []).map(this.mapHistoryItem)
        this.selectedLead.conversations = append
          ? [...this.selectedLead.conversations, ...history]
          : history
        this.leadHistoryPagination = {
          limit: payload.pagination?.limit || 50,
          offset: payload.pagination?.offset || 0,
          has_more: Boolean(payload.pagination?.has_more),
        }
      } catch (error) {
        if (this.selectedLead?.id !== selectedLeadId) return

        this.leadHistoryError = error.response?.data?.message
          || 'No fue posible cargar el historial del lead.'
      } finally {
        if (this.selectedLead?.id === selectedLeadId) {
          this.loadingLeadHistory = false
        }
      }
    },
    mapHistoryItem(item) {
      return {
        ...item,
        sender: item.type === 'incoming'
          ? this.selectedLead?.name || 'Lead'
          : item.sender || 'Agente Fresher',
        date: this.formatDateTime(item.timestamp),
      }
    },
    leadsByStage(stage) {
      return this.filteredLeads.filter((lead) => lead.statusKey === stage)
    },
    handleFileChange(event) {
      const file = event.target.files[0] || null
      this.selectedFile = file
      this.selectedFileName = file ? file.name : ''
    },
    downloadTemplate() {
      const exampleRow = {
        first_name: 'Ana',
        last_name: 'Rodriguez',
        email: 'ana@comercialnorte.com',
        phone: '5555555555',
        workflow_id: '',
        campaign_id: '',
        'metadata.lead_type': 'company_contact',
        'metadata.company_name': 'Comercial del Norte',
        'metadata.role': 'Directora de Finanzas',
        'metadata.profile': 'Exportador de aguacate',
        'metadata.preferred_channel': 'whatsapp',
        'metadata.source': 'LinkedIn',
        'metadata.notes': 'Prefiere contacto por WhatsApp',
      }

      const worksheet = XLSX.utils.json_to_sheet([exampleRow], { header: this.templateColumns })
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads')

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

      saveAs(blob, 'template_leads_fresher.xlsx')
    },
    openBulkUploadDialog() {
      this.leadFormMode = 'bulk'
      this.uploadDialog = true
    },
    submitLeadForm() {
      if (this.leadFormMode === 'single') {
        this.createLead()
      } else {
        this.importLeadsBulk()
      }
    },
    async createLead() {
      const firstName = this.leadForm.first_name.trim()
      const phone = this.leadForm.phone.trim()
      const email = this.leadForm.email.trim()

      if (!firstName) {
        this.mixError('El nombre del lead es obligatorio')
        return
      }
      if (!phone && !email) {
        this.mixError('Se requiere telefono o email para crear el lead')
        return
      }
      if (this.leadForm.forceFirstMessage.channel && !this.leadForm.forceFirstMessage.content.trim()) {
        this.mixError('Escribe el contenido del primer mensaje o quita el canal forzado')
        return
      }
      if (this.leadForm.forceFirstMessage.channel === 'email' && !this.leadForm.forceFirstMessage.subject.trim()) {
        this.mixError('El asunto es obligatorio cuando el primer mensaje es por email')
        return
      }

      this.creatingLead = true

      try {
        const payload = {
          first_name: firstName,
          last_name: this.leadForm.last_name.trim() || undefined,
          email: email || undefined,
          phone: phone || undefined,
          workflow_id: this.leadForm.workflow_id.trim() || undefined,
          campaign_id: this.leadForm.campaign_id.trim() || undefined,
          force_first_message: this.leadForm.forceFirstMessage.channel
            ? {
              channel: this.leadForm.forceFirstMessage.channel,
              content: this.leadForm.forceFirstMessage.content.trim(),
              subject: this.leadForm.forceFirstMessage.channel === 'email'
                ? this.leadForm.forceFirstMessage.subject.trim()
                : undefined,
            }
            : undefined,
          metadata: this.leadForm.metadata,
        }

        await DB.post(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/leads/createLead`,
          payload,
          this.getRequestConfig()
        )

        this.mixSuccess('Lead creado correctamente')
        this.uploadDialog = false
        this.resetLeadForm()
        this.getLeads()
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible crear el lead.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.creatingLead = false
      }
    },
    resetLeadForm() {
      this.leadForm = {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        workflow_id: '',
        campaign_id: '',
        forceFirstMessage: { channel: '', content: '', subject: '' },
        metadata: {
          lead_type: 'company_contact',
          company_name: '',
          role: '',
          profile: '',
          preferred_channel: 'whatsapp',
          source: 'manual',
          notes: '',
        },
      }
    },
    async importLeadsBulk() {
      if (!this.selectedFile) {
        this.mixError('Selecciona un archivo antes de continuar')
        return
      }

      this.importingLeads = true

      try {
        const formData = new FormData()
        formData.append('file', this.selectedFile)

        const response = await DB.post(
          `${URI}/api/v1/${this.selectLanguage || 'en'}/leads/importLeads`,
          formData,
          {
            headers: {
              Authorization: this.$store.state.sToken
                ? `Bearer ${this.$store.state.sToken}`
                : '',
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        const summary = response.data?.data || {}

        this.mixSuccess(
          `Leads importados: ${summary.created_rows || 0} creados, `
          + `${summary.updated_rows || 0} actualizados, ${summary.failed_rows || 0} con error.`
        )

        this.uploadDialog = false
        this.selectedFile = null
        this.selectedFileName = ''
        this.getLeads()
      } catch (error) {
        const message = error.response?.data?.message || 'No fue posible importar los leads.'
        this.mixError(message, error.response?.status || 400)
      } finally {
        this.importingLeads = false
      }
    },
  },
}
</script>

<style scoped>
.leads-agent {
  min-height: 100vh;
  padding: 46px 56px 72px;
  background: #f8fafc;
  color: #09090b;
  font-family: Inter, Roboto, Arial, sans-serif;
}

.leads-header,
.leads-title,
.card-heading,
.lead-row,
.lead-actions,
.tabs-row {
  display: flex;
  align-items: center;
}

.leads-header {
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.agent-alert {
  margin: 16px 0;
}

.alert-action {
  margin-left: 12px;
  color: inherit;
  font-weight: 700;
  text-decoration: underline;
}

.agent-progress {
  margin: 10px 0 14px;
}

.sync-caption {
  margin: 0 0 14px;
  color: #64748b;
  font-size: 13px;
  text-align: right;
}

.leads-title {
  gap: 16px;
}

.agent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: linear-gradient(135deg, #0f82c3, #246bfe);
  box-shadow: 0 14px 28px rgba(15, 130, 195, 0.22);
}

h1,
h2,
h3,
p {
  margin: 0;
}

.leads-title h1 {
  font-size: 38px;
  font-weight: 900;
  letter-spacing: 0;
}

.leads-title p,
.card-heading p {
  margin-top: 8px;
  color: #475569;
  font-size: 17px;
  font-weight: 600;
}

.primary-action,
.secondary-action {
  min-height: 48px !important;
  border-radius: 10px !important;
  background: linear-gradient(90deg, #246bfe, #0f82c3) !important;
  color: #fff !important;
  font-family: "montserrat-semibold";
  font-size: 15px !important;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action.small {
  min-height: 40px !important;
}

.secondary-action {
  background: #0f82c3 !important;
}

.insight-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 28px;
  padding: 18px 22px;
  border: 1px solid rgba(15, 130, 195, 0.16);
  border-radius: 16px;
  background:
    linear-gradient(90deg, rgba(15, 130, 195, 0.1), rgba(36, 107, 254, 0.06)),
    #ffffff;
}

.insight-banner span {
  display: block;
  margin-bottom: 4px;
  color: #0f82c3;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.insight-banner strong {
  color: #0f172a;
  font-size: 16px;
}

.insight-banner button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  color: #0f82c3;
  cursor: pointer;
  font-weight: 900;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  margin-bottom: 34px;
}

.metric-card,
.content-card,
.upload-dialog,
.lead-dialog {
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.metric-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 190px;
  padding: 28px 30px;
}

.metric-card span,
.metric-card small {
  display: block;
  color: #64748b;
  font-weight: 800;
}

.metric-card span {
  font-size: 16px;
}

.metric-card strong {
  display: block;
  margin: 38px 0 24px;
  color: #020617;
  font-size: 32px;
  font-weight: 900;
}

.metric-card small {
  font-size: 14px;
}

.metric-card:nth-child(2) small {
  color: #16a34a;
}

.tabs-row {
  display: inline-flex;
  gap: 4px;
  margin-bottom: 28px;
  padding: 4px;
  border-radius: 16px;
  background: #e9ebf0;
}

.tabs-row button {
  min-height: 38px;
  padding: 0 14px;
  border: 0;
  border-radius: 13px;
  background: transparent;
  color: #18181b;
  cursor: pointer;
  font-size: 16px;
  font-weight: 900;
}

.tabs-row button.active {
  background: #fff;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.tabs-row button.disabled {
  color: #a1a1aa;
  cursor: not-allowed;
}

.content-card {
  padding: 30px 32px;
}

.card-heading {
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 24px;
}

.card-heading h2 {
  font-size: 25px;
  font-weight: 900;
}

.link-action {
  color: #0f82c3 !important;
  font-family: "montserrat-semibold";
  text-transform: none;
}

.crm-toolbar {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 190px 170px auto;
  gap: 12px;
  margin-bottom: 18px;
}

.search-field {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #d8dee8;
  border-radius: 12px;
  background: #fff;
}

.search-field input,
.crm-toolbar select {
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  color: #0f172a;
  font-size: 14px;
  font-weight: 800;
}

.crm-toolbar select {
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid #d8dee8;
  border-radius: 12px;
  background: #fff;
}

.view-switch {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 14px;
  background: #e9ebf0;
}

.view-switch button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: #475569;
  cursor: pointer;
  font-weight: 900;
}

.view-switch button.active {
  background: #fff;
  color: #0f82c3;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.crm-table {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
}

.crm-table-head,
.crm-table-row {
  display: grid;
  grid-template-columns: 1.35fr 1fr 0.75fr 0.8fr 0.65fr 0.9fr 1.25fr;
  align-items: center;
  gap: 14px;
}

.crm-table-head {
  min-height: 46px;
  padding: 0 18px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.crm-table-row {
  width: 100%;
  min-height: 76px;
  padding: 12px 18px;
  border: 0;
  border-top: 1px solid #eef2f7;
  background: #fff;
  color: #334155;
  cursor: pointer;
  text-align: left;
  transition: background 0.16s ease, transform 0.16s ease;
}

.crm-table-row:hover {
  background: #f8fbff;
}

.crm-table-row strong {
  display: block;
  color: #0f172a;
  font-size: 15px;
  font-weight: 900;
}

.crm-table-row small {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
}

.crm-table-row span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.next-step-cell {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: help;
}

.crm-table-row span:nth-child(3) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.crm-table-row span:last-child {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  white-space: normal;
}

.crm-table-row em,
.pipeline-card em {
  font-style: normal;
  color: #0f82c3;
  font-size: 12px;
  font-weight: 900;
}

.empty-state,
.pipeline-empty {
  padding: 22px;
  color: #64748b;
  font-weight: 800;
  text-align: center;
}

.table-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 20px;
}

.table-pagination > span {
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.pipeline-board {
  display: grid;
  grid-template-columns: repeat(6, minmax(210px, 1fr));
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 6px;
}

.pipeline-column {
  min-height: 420px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #f8fafc;
}

.pipeline-column header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  color: #334155;
  font-size: 13px;
  font-weight: 900;
}

.pipeline-column header strong {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  min-height: 26px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0f82c3;
  font-size: 12px;
}

.pipeline-card {
  display: grid;
  gap: 7px;
  width: 100%;
  margin-bottom: 10px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  color: #0f172a;
  cursor: pointer;
  text-align: left;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04);
}

.pipeline-card strong {
  font-size: 15px;
  font-weight: 900;
}

.pipeline-card span,
.pipeline-card small {
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
}

.pipeline-card small {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.lead-drawer {
  z-index: 1000;
}

.v-dialog__content,
.v-overlay {
  z-index: 1100 !important;
}

.lead-drawer-content {
  display: grid;
  gap: 16px;
  min-height: 100%;
  padding: 24px;
  background: #f8fafc;
}

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.drawer-header h2 {
  color: #0f172a;
  font-size: 28px;
  font-weight: 900;
}

.drawer-header p {
  margin-top: 6px;
  color: #64748b;
  font-size: 15px;
  font-weight: 800;
}

.drawer-header button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid #d8dee8;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
}

.drawer-status-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.drawer-status-row > span:not(.status-pill) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 900;
}

.drawer-panel {
  padding: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
}

.drawer-panel h3 {
  margin-bottom: 14px;
  color: #0f172a;
  font-size: 19px;
  font-weight: 900;
}

.qualification-list.compact {
  grid-template-columns: 1fr;
}

.chat-panel {
  padding: 14px;
}

.drawer-actions {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin: 0 -24px -24px;
  padding: 14px 24px;
  border-top: 1px solid #e5e7eb;
  background: rgba(248, 250, 252, 0.94);
  backdrop-filter: blur(10px);
}

.leads-list,
.workflow-list,
.timeline-list {
  display: grid;
  gap: 14px;
}

.lead-row,
.workflow-row {
  justify-content: space-between;
  gap: 20px;
  padding: 22px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
}

.lead-main h3,
.workflow-row h3,
.product-card h3 {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  font-size: 22px;
  font-weight: 900;
}

.lead-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 12px;
}

.lead-meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  font-size: 16px;
  font-weight: 800;
}

.lead-actions {
  justify-content: flex-end;
  gap: 14px;
}

.status-pill,
.score-tag {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 11px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 900;
}

.status-pill.progress,
.score-tag.medium {
  background: #eef2ff;
  color: #3730a3;
}

.status-pill.neutral,
.score-tag.low {
  background: #f1f5f9;
  color: #334155;
}

.status-pill.success,
.score-tag.high {
  background: #050517;
  color: #fff;
}

.details-button {
  min-height: 40px !important;
  border-radius: 10px !important;
  border-color: #d8dee8 !important;
  color: #18181b !important;
  font-family: "montserrat-semibold";
  text-transform: none;
}

.danger-button {
  border-color: #fca5a5 !important;
  color: #b91c1c !important;
}

.confirm-dialog {
  padding: 8px;
}

.danger-action {
  background: #dc2626 !important;
  color: #fff !important;
  text-transform: none;
  border-radius: 10px !important;
}

.products-grid,
.template-grid,
.detail-grid,
.upload-grid {
  display: grid;
  gap: 20px;
}

.products-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.price-list-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 18px;
  margin-bottom: 20px;
  padding: 20px;
  border: 1px solid #dbeafe;
  border-radius: 16px;
  background: #f8fbff;
}

.price-list-panel label,
.upload-options label {
  display: grid;
  gap: 7px;
}

.price-list-panel label span,
.upload-options label span {
  color: #334155;
  font-size: 13px;
  font-weight: 900;
}

.price-list-panel textarea,
.upload-options textarea {
  width: 100%;
  min-height: 128px;
  padding: 12px;
  border: 1px solid #d8dee8;
  border-radius: 12px;
  background: #fff;
  color: #0f172a;
  font-family: Inter, Roboto, Arial, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  resize: vertical;
}

.price-list-note {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 14px;
  background: #ffffff;
}

.price-list-note p {
  color: #475569;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
}

.product-card,
.template-card,
.detail-panel,
.mapping-preview {
  padding: 22px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
}

.product-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  margin-bottom: 18px;
  border-radius: 14px;
  background: #e0f2fe;
}

.product-card p {
  margin-top: 12px;
  color: #475569;
  font-weight: 700;
  line-height: 1.5;
}

.product-tags,
.workflow-channels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.product-tags span,
.workflow-channels span {
  padding: 6px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  font-weight: 900;
}

.workflow-row {
  display: flex;
  align-items: center;
}

.workflow-row p {
  margin-top: 10px;
  color: #475569;
  font-size: 16px;
  font-weight: 700;
}

.timeline-item {
  position: relative;
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: 14px;
  padding-bottom: 18px;
}

.timeline-item:not(:last-child)::before {
  content: "";
  position: absolute;
  top: 18px;
  bottom: -4px;
  left: 8px;
  width: 2px;
  background: #dbeafe;
}

.timeline-dot {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: 4px solid #dbeafe;
  border-radius: 50%;
  background: #0f82c3;
}

.timeline-item strong {
  color: #0f172a;
  font-size: 17px;
}

.timeline-item p {
  margin: 5px 0;
  color: #475569;
  font-weight: 700;
}

.timeline-item small {
  color: #94a3b8;
  font-weight: 800;
}

.template-grid {
  grid-template-columns: 1fr 1fr;
}

.template-card h3,
.detail-panel h3,
.mapping-preview h3 {
  margin-bottom: 16px;
  font-size: 19px;
  font-weight: 900;
}

.columns-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.columns-grid span {
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  color: #334155;
  font-size: 13px;
  font-weight: 900;
}

.template-card ul {
  display: grid;
  gap: 12px;
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-weight: 700;
}

.upload-dialog h2,
.lead-dialog h2 {
  font-size: 25px;
  font-weight: 900;
}

.upload-dialog .v-card__title,
.lead-dialog .v-card__title {
  padding: 26px 28px 8px;
}

.upload-dialog .v-card__title p,
.lead-dialog .v-card__title p {
  margin-top: 8px;
  color: #64748b;
  font-size: 15px;
  font-weight: 700;
}

.upload-dialog .v-card__text,
.lead-dialog .v-card__text {
  padding: 22px 28px;
}

.upload-grid {
  grid-template-columns: minmax(0, 1fr) 330px;
}

.mode-switch {
  display: inline-flex;
  gap: 4px;
  margin-bottom: 20px;
  padding: 4px;
  border-radius: 14px;
  background: #e9ebf0;
}

.mode-switch button {
  min-height: 38px;
  padding: 0 16px;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: #334155;
  cursor: pointer;
  font-weight: 900;
}

.mode-switch button.active {
  background: #fff;
  color: #0f82c3;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.single-lead-form,
.bulk-lead-form {
  display: grid;
  gap: 20px;
}

.form-grid {
  display: grid;
  gap: 14px;
}

.form-grid.two-columns {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.form-grid label,
.full-field {
  display: grid;
  gap: 7px;
}

.form-grid label span,
.full-field span {
  color: #334155;
  font-size: 13px;
  font-weight: 900;
}

.form-grid input,
.form-grid select,
.full-field textarea,
.upload-options input,
.upload-options textarea {
  width: 100%;
  min-height: 42px;
  padding: 0 12px;
  border: 1px solid #d8dee8;
  border-radius: 10px;
  background: #fff;
  color: #0f172a;
  font-family: Inter, Roboto, Arial, sans-serif;
  font-size: 14px;
  font-weight: 800;
}

.full-field textarea,
.upload-options textarea {
  min-height: 108px;
  padding: 12px;
  line-height: 1.5;
  resize: vertical;
}

.metadata-panel {
  display: grid;
  gap: 14px;
  padding: 18px;
  border: 1px solid #dbeafe;
  border-radius: 16px;
  background: #f8fbff;
}

.metadata-panel h3 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 900;
}

.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  gap: 10px;
  border: 2px dashed #bfdbfe;
  border-radius: 16px;
  background: #f8fbff;
  cursor: pointer;
}

.dropzone strong {
  color: #0f172a;
  font-size: 18px;
}

.dropzone span {
  color: #64748b;
  font-weight: 700;
}

.hidden-input {
  display: none;
}

.upload-options {
  display: grid;
  gap: 14px;
}

.upload-options select {
  min-height: 42px;
  padding: 0 12px;
  border: 1px solid #d8dee8;
  border-radius: 10px;
  background: #fff;
  color: #0f172a;
  font-weight: 800;
}

.checkbox-row {
  display: flex !important;
  grid-template-columns: none;
  align-items: center;
  gap: 10px !important;
}

.mapping-preview {
  margin-top: 20px;
  background: #f8fafc;
}

.preview-table {
  display: grid;
  gap: 8px;
}

.preview-row {
  display: grid;
  grid-template-columns: 1fr 1.1fr 0.9fr 0.8fr 0.7fr;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  color: #334155;
  font-size: 13px;
  font-weight: 800;
}

.detail-grid {
  grid-template-columns: 0.95fr 1.05fr;
}

.qualification-list {
  display: grid;
  gap: 10px;
}

.qualification-list article,
.next-step {
  padding: 14px;
  border-radius: 12px;
  background: #f8fafc;
}

.qualification-list span,
.next-step span {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.qualification-list strong,
.next-step strong {
  display: block;
  margin-top: 6px;
  color: #0f172a;
  font-size: 15px;
}

.next-step {
  margin-top: 16px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
}

.chat-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.chat-heading h3 {
  margin: 0;
}

.chat-heading p {
  margin-top: 4px;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
}

.whatsapp-chat {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 540px;
  padding: 16px;
  border-radius: 18px;
  background:
    linear-gradient(rgba(226, 232, 240, 0.88), rgba(226, 232, 240, 0.88)),
    radial-gradient(circle at 15% 20%, rgba(15, 130, 195, 0.08), transparent 25%),
    #dfe5e8;
  overflow: hidden;
}

.chat-empty-state {
  max-width: 360px;
  margin: 28px auto;
  padding: 18px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.72);
  color: #64748b;
  text-align: center;
}

.history-progress,
.history-alert {
  margin-bottom: 14px;
}

.history-more {
  align-self: center;
  margin: 8px 0 14px;
  color: #0f82c3 !important;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.chat-date {
  align-self: center;
  margin-bottom: 16px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #fff;
  color: #111827;
  font-size: 13px;
  font-weight: 900;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.chat-message {
  display: flex;
  margin-bottom: 10px;
}

.chat-message.incoming {
  justify-content: flex-start;
}

.chat-message.outgoing {
  justify-content: flex-end;
}

.chat-message.system {
  justify-content: center;
  margin: 14px 0;
}

.chat-bubble {
  max-width: 78%;
  padding: 10px 12px 8px;
  border-radius: 12px;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.chat-message.incoming .chat-bubble {
  border-top-left-radius: 4px;
  background: #fff;
}

.chat-message.outgoing .chat-bubble {
  border-top-right-radius: 4px;
  background: #c9facc;
}

.chat-bubble strong {
  display: block;
  margin-bottom: 4px;
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
}

.chat-bubble b {
  display: block;
  margin-bottom: 5px;
  color: #0f172a;
  font-size: 13px;
}

.chat-bubble p {
  color: #0f172a;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.45;
}

.chat-bubble small {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 6px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
}

.chat-system-line {
  display: grid;
  grid-template-columns: minmax(28px, 1fr) auto minmax(28px, 1fr);
  align-items: center;
  width: 100%;
  gap: 10px;
  color: #334155;
  font-size: 12px;
  font-weight: 900;
}

.chat-system-line span {
  height: 1px;
  background: rgba(100, 116, 139, 0.28);
}

.chat-system-line strong {
  max-width: 330px;
  text-align: center;
}

.chat-system-line strong small {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-weight: 700;
}

.chat-composer {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding: 10px 10px 10px 14px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.1);
}

.chat-composer span {
  flex: 1;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
}

.chat-composer button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 0 13px;
  border: 0;
  border-radius: 10px;
  background: #1f2937;
  color: #fff;
  cursor: pointer;
  font-weight: 900;
}

@media (max-width: 1180px) {
  .crm-toolbar {
    grid-template-columns: 1fr 1fr;
  }

  .crm-table {
    overflow-x: auto;
  }

  .crm-table-head,
  .crm-table-row {
    min-width: 1120px;
  }

  .metrics-grid,
  .products-grid,
  .price-list-panel,
  .template-grid,
  .detail-grid,
  .upload-grid,
  .form-grid.two-columns {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .leads-agent {
    padding: 28px 18px 54px;
  }

  .leads-header,
  .card-heading,
  .lead-row,
  .workflow-row,
  .insight-banner {
    align-items: flex-start;
    flex-direction: column;
  }

  .primary-action,
  .secondary-action {
    width: 100%;
  }

  .header-actions,
  .header-actions .v-btn {
    width: 100%;
  }

  .table-pagination {
    align-items: stretch;
    flex-direction: column;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .crm-toolbar {
    grid-template-columns: 1fr;
  }

  .view-switch {
    width: 100%;
  }

  .view-switch button {
    flex: 1;
    justify-content: center;
  }

  .lead-drawer {
    width: 100% !important;
  }

  .lead-drawer-content {
    padding: 18px;
  }

  .drawer-actions {
    align-items: stretch;
    flex-direction: column;
    margin: 0 -18px -18px;
    padding: 14px 18px;
  }

  .preview-row {
    grid-template-columns: 1fr;
  }
}
</style>
